import * as wjcCore from '@mescius/wijmo';
import * as wjcGrid from '@mescius/wijmo.grid';
import {ViewChild, Component, OnInit} from '@angular/core';
import {ToasterService} from 'angular2-toaster';
import {DataService, OrientDBCollectionView, OUser} from '@cosbeni/database';
import {ActivePromotionConfig} from '../config/active-promotion.config';
import {KobeacService} from './kobeac.service';
import {ROLES} from '../services/roles';
import {TranslocoService} from '@jsverse/transloco';

/**
 * kobe-ac ユーザ管理 (旧「会員管理」を拡張)。神戸オークションラインの OUser
 * (神戸オークションユーザ / 神戸オークション管理者) を作成・編集・パスワード変更し、
 * 併せて OUser に AC_Customer を紐付ける (紐付けたユーザは書類画面で自分の顧客
 * フォルダ=売買台帳PDF 等のみ閲覧可)。UI はオンラインオークションのユーザ管理に合わせ、
 * 左に一覧グリッド・右に作成/編集フォーム (mode: view/edit/confirm) を置く。
 * 割当可能ロールは神戸ラインの 2 つに限定し、一覧も神戸ロール保有ユーザのみを表示する。
 */
@Component({
    standalone: false,
    selector: 'kobeac-member',
    templateUrl: 'member.component.html',
})
export class KobeacMemberComponent implements OnInit {
    readonly: Boolean = true;
    @ViewChild('grid') grid: wjcGrid.FlexGrid;
    users: OrientDBCollectionView;
    /** AC_Customer 一覧 (顧客紐付け用)。 */
    customers: any[] = [];
    /** 割当可能ロール (神戸オークションユーザ / 神戸オークション管理者) のみ。 */
    assignableRoles: any[] = [];
    private allRoles: any[] = [];
    user: any = new OUser();
    selectedRoleName: string = ROLES.KOBE_USER;
    selectedCustomerRid: string = '';
    password: string = '';
    confirmPassword: string = '';
    newPassword: string = '';
    confirmNewPassword: string = '';
    mode: string = 'view';

    /** この画面が扱う神戸ラインのロール名。 */
    private readonly kobeRoleNames: string[] = [ROLES.KOBE_USER, ROLES.KOBE_ADMIN];

    constructor(public dataService: DataService, private kobeac: KobeacService, public toasterService: ToasterService,
                private transloco: TranslocoService) {
        const scope = "roles CONTAINS (name IN ['" + ROLES.KOBE_USER + "','" + ROLES.KOBE_ADMIN + "'])";
        this.users = new OrientDBCollectionView(dataService, ActivePromotionConfig.DATABASE, 'SELECT FROM OUser', 'OUser', '', 0, 100, true, true, false);
        this.users.url = ActivePromotionConfig.SERVER_URL;
        this.users.tokenName = ActivePromotionConfig.DATABASE + '_token';
        this.users.filterSpecial = scope;
        this.users.filterOnServer = true;
    }

    async ngOnInit(): Promise<void> {
        this.users.currentChanged.addHandler(this.onCurrentChanged, this);
        await this.kobeac.ensureLoaded();
        this.customers = this.kobeac.customers;
        this.dataService.query({sql: 'SELECT FROM ORole', count: 'SELECT FROM ORole'}, ActivePromotionConfig.SERVER_URL).subscribe((res: any) => {
            this.allRoles = wjcCore.isArray(res.results) ? res.results : [];
            this.assignableRoles = this.allRoles.filter((r) => this.kobeRoleNames.indexOf(r.name) >= 0);
        });
    }

    onCurrentChanged(sender: any, args: wjcCore.EventArgs): void {
        if (this.users.currentItem != null) {
            this.user = Object.assign({}, this.users.currentItem);
            this.selectedRoleName = this.kobeRoleOf(this.user.roles);
            this.selectedCustomerRid = this.customerRidOf(this.user.customer);
            this.mode = 'view';
        }
    }

    /** OUser.roles から神戸ラインのロール名を取り出す (無ければ神戸ユーザ既定)。 */
    private kobeRoleOf(roles: any): string {
        if (wjcCore.isArray(roles)) {
            for (const r of roles) {
                const name = wjcCore.isObject(r) ? r.name : r;
                if (this.kobeRoleNames.indexOf(name) >= 0) {
                    return name;
                }
            }
        }
        return ROLES.KOBE_USER;
    }

    /** OUser.customer (link or rid) から @rid 文字列を得る。 */
    private customerRidOf(customer: any): string {
        if (wjcCore.isObject(customer)) {
            return customer['@rid'] || '';
        }
        if (wjcCore.isString(customer)) {
            return customer;
        }
        return '';
    }

    private roleObj(name: string): any {
        for (const r of this.allRoles) {
            if (r.name === name) {
                return r;
            }
        }
        return null;
    }

    private roleRid(name: string): string {
        const r = this.roleObj(name);
        return r ? r['@rid'] : '';
    }

    /** グリッドの顧客列に表示するラベル (ID + 名称)。 */
    customerLabelOf(item: any): string {
        const rid = this.customerRidOf(item ? item.customer : null);
        if (!rid) {
            return '';
        }
        for (const c of this.customers) {
            if (c['@rid'] === rid) {
                return c['ID'] + ' ' + c['名称'];
            }
        }
        return '';
    }

    customerLabel(c: any): string {
        return c['ID'] + ' ' + c['名称'];
    }

    get isNew(): boolean {
        return this.user && this.user['@rid'] == '#-1:-1';
    }

    /** email は空 (任意) か、メールアドレスの形になっていれば OK。 */
    get emailValid(): boolean {
        const e = (this.user && this.user.email ? String(this.user.email) : '').trim();
        return !e || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
    }

    get createValid(): boolean {
        return !wjcCore.isNullOrWhiteSpace(this.user.name) && this.user.name.length >= 3
            && !wjcCore.isNullOrWhiteSpace(this.password) && this.password.length >= 3
            && this.password === this.confirmPassword
            && !wjcCore.isNullOrWhiteSpace(this.selectedRoleName)
            && this.emailValid;
    }

    get editValid(): boolean {
        return !wjcCore.isNullOrWhiteSpace(this.user.name) && this.user.name.length >= 3
            && !wjcCore.isNullOrWhiteSpace(this.selectedRoleName)
            && this.emailValid;
    }

    get passwordValid(): boolean {
        return !wjcCore.isNullOrWhiteSpace(this.newPassword) && this.newPassword.length >= 3
            && this.newPassword === this.confirmNewPassword;
    }

    editUser(): void {
        this.mode = 'edit';
    }

    confirmUser(): void {
        this.mode = 'confirm';
    }

    createUser(): void {
        this.mode = 'edit';
        this.user = new OUser();
        this.user.status = 'ACTIVE';
        this.selectedRoleName = ROLES.KOBE_USER;
        this.selectedCustomerRid = '';
        this.password = '';
        this.confirmPassword = '';
    }

    /** ユーザ (名前/ステータス/ロール/顧客リンク) を保存する。LINK は SQL で設定する。 */
    saveUser(): void {
        const roleRid = this.roleRid(this.selectedRoleName);
        const customer = this.selectedCustomerRid ? this.selectedCustomerRid : 'null';
        const props: string[] = [];
        props.push("name = '" + this.escape(this.user.name) + "'");
        props.push("status = '" + this.escape(this.user.status || 'ACTIVE') + "'");
        props.push("email = '" + this.escape((this.user.email || '').trim()) + "'");
        if (roleRid) {
            props.push('roles = [' + roleRid + ']');
        }
        props.push('customer = ' + customer);
        let sql = 'BEGIN\n';
        if (this.isNew) {
            props.push("password = '" + this.escape(this.password) + "'");
            sql = sql + 'INSERT INTO OUser SET ' + props.join(', ') + '\n';
        } else {
            sql = sql + 'UPDATE OUser SET ' + props.join(', ') + ' WHERE @rid = ' + this.user['@rid'] + '\n';
        }
        sql = sql + 'COMMIT RETRY 100\n';
        this.dataService.batch({sql: sql}, ActivePromotionConfig.SERVER_URL).subscribe(() => {
            this.mode = 'view';
            this.toasterService.pop('info', this.transloco.translate('common.save'), this.transloco.translate('kobeac.saved_passive_period'));
            this.users.queryData();
        }, () => {
            this.toasterService.pop('error', this.transloco.translate('common.save'), this.transloco.translate('kobeac.failed_period'));
        });
    }

    changePassword(): void {
        const command = 'UPDATE ' + this.user['@rid'] + " SET password = '" + this.escape(this.newPassword) + "'";
        this.dataService.command({sql: command}, ActivePromotionConfig.SERVER_URL).subscribe(() => {
            this.mode = 'view';
            this.newPassword = '';
            this.confirmNewPassword = '';
            this.toasterService.pop('info', this.transloco.translate('kobeac.change_password'), this.transloco.translate('kobeac.changed_period'));
            this.users.queryData();
        }, () => {
            this.toasterService.pop('error', this.transloco.translate('kobeac.change_password'), this.transloco.translate('kobeac.failed_period'));
        });
    }

    private escape(v: string): string {
        return String(v == null ? '' : v).replace(/\\/g, '\\\\').replace(/'/g, '\\\'');
    }
}
